import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  motion,
  AnimatePresence,
  useScroll,
  useSpring,
  useTransform,
} from "framer-motion";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet";

import SiteHeader from "../components/SiteHeader";
import Footer from "../components/Footer";
import BackToTop from "../components/BackToTop";
import PageViewCounter from "../components/PageViewCounter";

import { api, fetchJsonWithProxies } from "../lib/omekaClient";
import {
  titleOf,
  descOf,
  publisherOf,
  extentOf,
  languageOf,
  subjectOf,
} from "../lib/textOf";
import {
  quickFactFields,
  bibliographicFieldsLeft,
  bibliographicFieldsRight,
} from "../data/bookArticleDetailFields";

/* helpers */
const normalizeHttps = (u) =>
  u ? (u.startsWith("http://") ? u.replace(/^http:/, "https:") : u) : "";

const pickLang = (field, lang) => {
  if (!field || !Array.isArray(field)) return null;
  const match = field.find((val) => val["@language"] === lang);
  if (match) return match["@value"];
  const noLang = field.find((val) => !val["@language"]);
  if (noLang) return noLang["@value"];
  return field[0]?.["@value"] || null;
};

/* Skeleton Loader */
function SkeletonDetail() {
  return (
    <div className="min-h-screen flex flex-col pt-32 px-6 items-center bg-[#faf7f2] animate-pulse">
      <div className="h-10 w-3/4 max-w-2xl bg-[#e5d7ca]/50 rounded-lg mb-4"></div>
      <div className="h-6 w-1/2 max-w-md bg-[#e5d7ca]/40 rounded-lg mb-10"></div>
      <div className="flex w-full max-w-6xl gap-10">
        <div className="w-1/3 h-[500px] bg-[#e5d7ca]/40 rounded-3xl"></div>
        <div className="w-2/3 h-96 bg-[#e5d7ca]/30 rounded-3xl"></div>
      </div>
    </div>
  );
}

/* Parallax Image */
function ParallaxImage({ src, alt, className = "" }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [0, -40]);
  const smoothY = useSpring(y, { stiffness: 100, damping: 20 });

  return (
    <motion.figure
      ref={ref}
      style={{ y: smoothY }}
      className={`overflow-hidden rounded-3xl shadow-xl ring-1 ring-[#eadfce]/50 ${className}`}
    >
      <img
        src={src}
        alt={alt}
        className="w-full h-full object-cover"
        loading="lazy"
      />
    </motion.figure>
  );
}

/* ---------------- AUDIO BANNER COMPONENT (Floating Player) ---------------- */
function AudioBanner({ audioUrl, t }) {
  if (!audioUrl) return null;

  return (
    <div className="fixed bottom-6 left-0 w-full z-[100] flex justify-center pointer-events-none px-4 transition-all duration-300">
      {/* ตัวเครื่องเล่นเสียง */}
      <div className="w-full max-w-md pointer-events-auto shadow-2xl rounded-full overflow-hidden border border-white/20 bg-white/50 backdrop-blur-md">
        <audio
          className="w-full h-[50px] outline-none"
          controls
          autoPlay
          controlsList="nodownload"
          onContextMenu={(e) => e.preventDefault()}
          src={audioUrl}
        >
          {t("book.audioUnsupported", "เบราว์เซอร์ของคุณไม่รองรับการเล่นเสียง")}
        </audio>
      </div>
    </div>
  );
}
/* -------------------------------------------------------- */
/* -------------------------------------------------------- */

export default function BookArticleDetail() {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [item, setItem] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    if (window.snapSaveState) window.snapSaveState = () => { };

    (async () => {
      try {
        setLoading(true);

        // 1. ดึงข้อมูล Item หลัก
        const data = await fetchJsonWithProxies(api(`/items/${id}`));

        // 2. ดึงข้อมูล Media ที่เชื่อมกับ Item นี้
        const mediaData = await fetchJsonWithProxies(api(`/media?item_id=${id}`));

        if (!alive) return;

        // แนบข้อมูล mediaData เข้าไปเก็บไว้ใน state item ด้วยเลย
        setItem({ ...data, _mediaData: mediaData });

        if (window.snapSaveState) {
          setTimeout(() => {
            window.snapSaveState = null;
          }, 500);
        }
      } catch (e) {
        if (alive) setErr(e.message);
        if (window.snapSaveState) window.snapSaveState = null;
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [id, i18n.language]);

  const meta = useMemo(() => {
    if (!item) return {};
    const lang = i18n.language;
    const thumbUrl =
      item?.thumbnail_display_urls?.large ||
      item?.thumbnail_display_urls?.medium ||
      "/assets/placeholder.webp";

    // -------------------------------------------------------------
    // ฟังก์ชันค้นหาไฟล์เสียงจาก Media Tab
    let finalAudioUrl = pickLang(item["schema:audio"], lang) || "";

    if (!finalAudioUrl && item._mediaData && item._mediaData.length > 0) {
      const audioFiles = item._mediaData.filter(m => {
        const mimeType = m["o:media_type"]?.toLowerCase() || "";
        const filename = m["o:filename"]?.toLowerCase() || "";
        return mimeType.startsWith("audio/") || filename.endsWith(".m4a") || filename.endsWith(".mp3");
      });

      if (audioFiles.length > 0) {
        const matchedLangFile = audioFiles.find(m => {
          const mediaTitle = m["dcterms:title"]?.[0]?.["@value"]?.toLowerCase() || "";
          const filename = m["o:filename"]?.toLowerCase() || "";
          return mediaTitle.includes(lang) || filename.includes(`_${lang}`);
        });

        finalAudioUrl = matchedLangFile
          ? matchedLangFile["o:original_url"]
          : audioFiles[0]["o:original_url"];
      }
    }
    // -------------------------------------------------------------

    return {
      title: pickLang(item["dcterms:title"], lang) || item.title || titleOf(item),
      creator:
        pickLang(item["dcterms:creator"], lang) ||
        item.creator ||
        t("book.unknownCreator", "ไม่ระบุ"),

      date:
        pickLang(item["dcterms:date"], lang) ||
        item.date ||
        t("book.unknownDate", "ม.ป.ป."),
      cover: thumbUrl,
      rights: pickLang(item["dcterms:rights"], lang) || item.rights || "",
      type: pickLang(item["dcterms:type"], lang) || item.type || "",
      description: pickLang(item["dcterms:description"], lang) || item.description || descOf(item) || "",
      format: pickLang(item["dcterms:format"], lang) || item.format || extentOf(item) || "",
      language: pickLang(item["dcterms:language"], lang) || item.language || languageOf(item) || "",
      publisher: pickLang(item["dcterms:publisher"], lang) || item.publisher || publisherOf(item) || "",
      alternative: pickLang(item["dcterms:alternative"], lang) || item.alternative || "",
      contributor: pickLang(item["dcterms:contributor"], lang) || item.contributor || "",
      identifier: pickLang(item["dcterms:identifier"], lang) || item.identifier || "",
      subject: pickLang(item["dcterms:subject"], lang) || item.subject || subjectOf(item) || "",
      source: pickLang(item["dcterms:source"], lang) || item.source || "",
      relation: pickLang(item["dcterms:relation"], lang) || item.relation || "",
      coverage: pickLang(item["dcterms:coverage"], lang) || item.coverage || "",

      audioUrl: finalAudioUrl,
    };
  }, [item, i18n.language, t]);

  const tocEntries = useMemo(() => {
    if (!item) return [];
    const raw = item["dcterms:tableOfContents"] || [];
    return raw.map((entry, idx) => ({
      id: idx,
      label: entry?.["@value"] || "",
      abstract: entry?.["@annotation"]?.["dcterms:abstract"]?.[0]?.["@value"] || "",
    }));
  }, [item, i18n.language]);

  // ตัวช่วยแสดงฟิลด์รายละเอียดใน Section 03 ให้โค้ดดูสะอาดตา
  const MetaRow = ({ label, value }) => {
    if (!value) return null;
    return (
      <div className="border-b border-[#faf7f2] pb-4 last:border-0 last:pb-0">
        <strong className="text-[#3a2c22] block mb-1 uppercase tracking-wider text-[11px] opacity-70">
          {label}
        </strong>
        <span className="text-[#5b4a3e] text-[15px] leading-relaxed break-words">
          {value}
        </span>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#faf7f2] font-['Source_Serif_4',_serif] text-[#1b1b1b]">
      {meta.audioUrl && (
        <AudioBanner audioUrl={normalizeHttps(meta.audioUrl)} t={t} />
      )}

      <SiteHeader />

      <AnimatePresence mode="wait">
        {loading ? (
          <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.4 }}>
            <SkeletonDetail />
          </motion.div>
        ) : err ? (
          <motion.div key="error" className="min-h-screen grid place-items-center text-red-600 font-['DM_Sans',_sans-serif]">
            {t("errors.loadFailed", "โหลดข้อมูลไม่สำเร็จ")}
          </motion.div>
        ) : (
          <motion.div key={i18n.language} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.4 }}>
            <Helmet>
              <title>{`${meta.title} · ${t("book.showcase", "Showcase")}`}</title>
              <meta name="description" content={meta.description ? meta.description.substring(0, 160) : ""} />
              <meta property="og:type" content="book" />
              <meta property="og:title" content={meta.title} />
              <meta property="og:description" content={meta.description} />
              <meta property="og:image" content={normalizeHttps(meta.cover)} />
            </Helmet>

            {/* HERO SECTION */}
            <header className="bg-[#3a2c22] text-[#fdfbf7] pt-28 pb-20 mt-[-80px]">
              <div className="max-w-6xl mx-auto px-6 mt-10">
                {meta.subject && (
                  <p className="uppercase text-[#d8653b] font-['DM_Sans',_sans-serif] tracking-[0.2em] text-xs md:text-sm mb-4 font-bold">
                    {meta.subject.split(',')[0]}
                  </p>
                )}
                {/* เปลี่ยนจาก 4xl, 5xl, 7xl ให้เล็กลงตามความเหมาะสม เช่น 3xl, 4xl, 5xl */}
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-['Playfair_Display',_serif] italic font-bold mb-6 leading-tight max-w-4xl text-[#fff3e2]">
                  {meta.title}
                </h1>
                {meta.alternative && (
                  <h2 className="text-xl md:text-2xl opacity-80 font-['Playfair_Display',_serif] mb-6">
                    {meta.alternative}
                  </h2>
                )}
                <div className="flex flex-wrap items-center gap-4 font-['DM_Sans',_sans-serif] text-sm opacity-90 border-l-2 border-[#d8653b] pl-4">
                  <p>
                    {t("book.by", "โดย")}{" "}
                    <span className="font-semibold text-white">{meta.creator}</span>
                  </p>
                  <span className="w-1 h-1 rounded-full bg-[#fdfbf7] opacity-50"></span>
                  <p>{meta.date}</p>

                  {/* --- ส่วน Component นับยอดวิว --- */}
                  <span className="w-1 h-1 rounded-full bg-[#fdfbf7] opacity-50"></span>
                  <PageViewCounter id={id} t={t} />

                </div>
              </div>
            </header>

            {/* MAIN CONTENT */}
            <main className="max-w-7xl mx-auto px-6 py-16 -mt-12">
              <div className="flex flex-col md:flex-row gap-12 lg:gap-20 relative items-start">

                {/* LEFT: Sidebar (Sticky) - Quick Facts */}
                <aside className="w-full md:w-[320px] lg:w-[380px] shrink-0 md:sticky md:top-24 space-y-6 z-10">

                  {/* หุ้ม ParallaxImage ด้วย Link พร้อมกับเพิ่ม hover effect เพื่อให้ผู้ใช้รู้ว่ากดได้ */}
                  <Link
                    to={`/read/${id}`}
                    className="block cursor-pointer hover:opacity-90 hover:-translate-y-1 transition-all duration-300"
                    title={t("actions.readFullText", "อ่านฉบับเต็ม")}
                  >
                    <ParallaxImage
                      src={normalizeHttps(meta.cover)}
                      alt={meta.title || t("book.coverAlt", "ภาพปกเอกสาร")}
                      className="aspect-[3/4] w-full border-4 border-white"
                    />
                  </Link>

                  <div className="bg-white p-6 rounded-2xl shadow-sm border border-[#eadfce] font-['DM_Sans',_sans-serif] text-sm">
                    <h3 className="uppercase tracking-widest text-xs font-bold text-[#8b6b55] mb-5 border-b border-[#eadfce] pb-3">
                      {t("book.quickFacts", "ข้อมูลโดยย่อ")}
                    </h3>
                    <ul className="space-y-4 text-[#5b4a3e]">
                      {quickFactFields.map((field) => {
                        const value = meta[field.key];
                        if (!value) return null;

                        return (
                          <li key={field.key} className="flex flex-col gap-1">
                            <span className="opacity-60 text-[10px] uppercase tracking-wider font-bold">
                              {t(field.labelKey)}
                            </span>
                            <span className="font-medium text-[15px]">{value}</span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>

                  <Link
                    to={`/read/${id}`}
                    className="block w-full text-center px-6 py-4 rounded-xl bg-[#d8653b] text-white shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all font-['DM_Sans',_sans-serif] font-bold tracking-wide"
                  >
                    {t("actions.readFullText", "อ่านฉบับเต็ม")}
                  </Link>
                </aside>

                {/* RIGHT: Scrollable Content */}
                <article className="flex-1 space-y-16">

                  {/* Section 01: Overview */}
                  {meta.description && (
                    <section>
                      <div className="flex items-baseline gap-4 mb-6">
                        <h2 className="text-2xl font-bold font-['DM_Sans',_sans-serif] text-[#3a2c22] uppercase tracking-wide">
                          {t("book.overview", "สาระสังเขป")}
                        </h2>
                      </div>
                      <div className="prose prose-lg text-[#4a3a2e] leading-relaxed whitespace-pre-line border-l border-[#eadfce] pl-6">
                        {meta.description}
                      </div>
                    </section>
                  )}

                  {/* Section 02: Table of Contents */}
                  {tocEntries.length > 0 && (
                    <section>
                      <div className="flex items-baseline gap-4 mb-6">
                        <h2 className="text-2xl font-bold font-['DM_Sans',_sans-serif] text-[#3a2c22] uppercase tracking-wide">
                          {t("book.toc", "สารบัญ")}
                        </h2>
                      </div>
                      <div className="bg-white rounded-3xl p-8 border border-[#eadfce] shadow-sm">
                        <ol className="space-y-6 list-none p-0 m-0">
                          {tocEntries.map((toc, index) => (
                            <li key={toc.id} className="relative pl-10 border-b border-[#faf7f2] pb-6 last:border-0 last:pb-0">
                              <span className="absolute left-0 top-0 font-['Playfair_Display',_serif] font-bold text-xl text-[#d8653b]/40 italic">
                                {String(index + 1).padStart(2, '0')}
                              </span>
                              <h4 className="text-lg font-bold text-[#3a2c22] font-['DM_Sans',_sans-serif] leading-tight mb-2">
                                {toc.label}
                              </h4>
                              {toc.abstract && (
                                <p className="text-[15px] text-[#6b584b] leading-relaxed">
                                  {toc.abstract}
                                </p>
                              )}
                            </li>
                          ))}
                        </ol>
                      </div>
                    </section>
                  )}

                  {/* Section 03: FULL DETAILS / ข้อมูลทั้งหมด */}
                  <section>
                    <div className="flex items-baseline gap-4 mb-6">
                      <h2 className="text-2xl font-bold font-['DM_Sans',_sans-serif] text-[#3a2c22] uppercase tracking-wide">
                        {t("book.fullDetails", "ข้อมูลทางบรรณานุกรม")}
                      </h2>
                    </div>
                    <div className="bg-white rounded-3xl p-8 border border-[#eadfce] shadow-sm font-['DM_Sans',_sans-serif]">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6">
                        {/* ซ้าย */}
                        <div className="space-y-4">
                          {bibliographicFieldsLeft.map((field) => (
                            <MetaRow
                              key={field.key}
                              label={t(field.labelKey)}
                              value={meta[field.key]}
                            />
                          ))}
                        </div>

                        {/* ขวา */}
                        <div className="space-y-4">
                          {bibliographicFieldsRight.map((field) => (
                            <MetaRow
                              key={field.key}
                              label={t(field.labelKey)}
                              value={meta[field.key]}
                            />
                          ))}
                        </div>
                      </div>

                      {/* Rights section (เต็มความกว้างด้านล่าง) */}
                      {meta.rights && (
                        <div className="mt-8 pt-6 border-t-2 border-[#faf7f2]">
                          <strong className="text-[#3a2c22] block mb-2 uppercase tracking-wider text-[11px] opacity-70">
                            {t("book.detailFields.rights", "สิทธิและลิขสิทธิ์ (Rights)")}
                          </strong>
                          <span className="text-[#5b4a3e] text-[15px] leading-relaxed block p-4 bg-[#faf7f2] rounded-xl">
                            {meta.rights}
                          </span>
                        </div>
                      )}
                    </div>
                  </section>
                </article>

              </div>
            </main>

            {/* Dark CTA Bar ด้านล่าง */}
            <section className="bg-[#3a2c22] text-[#fdfbf7] py-16 text-center mt-12 font-['DM_Sans',_sans-serif]">
              <div className="max-w-2xl mx-auto px-6">
                <h2 className="text-3xl font-['Playfair_Display',_serif] italic mb-4">
                  {t("book.ctaTitle", "พร้อมเปิดอ่านแล้วหรือยัง?")}
                </h2>
                <p className="text-[#cfbca8] mb-8">
                  {t("book.readNowDesc", "คลิกเพื่อเปิดอ่านเอกสารฉบับเต็มผ่านระบบ Viewer ของเรา")}
                </p>
                <Link
                  to={`/read/${id}`}
                  className="inline-block px-10 py-4 rounded-full border border-[#d8653b] bg-[#d8653b] text-white hover:bg-transparent hover:text-[#d8653b] transition-all font-bold tracking-widest text-sm uppercase"
                >
                  {t("actions.openReader", "Open Reader")}
                </Link>
              </div>
            </section>

            <Footer />
            <BackToTop />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}